import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGraphFaceSideHandle } from "building-types/buildingGraph/buildingGraphSideHandles";
import type { WallPlane, WallPlaneSubdomain } from "building-types/walls2/wallPlane";
import { faceNodeGetVolumeNode, faceNodeGetVolumeNodeFromTable } from "../../buildingGraph/faceNodeParent";
import { faceSideHandleLeaf } from "../../buildingGraph/resolveFaceSide";

/**
 * Returns whether any face node in `faceNodeIds` belongs to a volume whose parent is
 * one of the given rooms. Used by wall plane selection highlighting and the room-edge View inspector.
 */
export function faceNodeIdsHaveRoomParentInSet(
  buildingGraph: Pick<BuildingGraph, "faceNodes" | "volumeNodes">,
  faceNodeIds: readonly string[],
  roomIds: ReadonlySet<string>,
): boolean {
  const { faceNodes, volumeNodes } = buildingGraph;
  for (const faceNodeId of faceNodeIds) {
    const faceNode = faceNodes[faceNodeId];
    if (!faceNode) {
      continue;
    }
    const volumeNode = faceNodeGetVolumeNodeFromTable(faceNode, volumeNodes);
    const parentHandle = volumeNode?.parentHandle;
    if (parentHandle?.type === "ROOM" && roomIds.has(parentHandle.roomId)) {
      return true;
    }
  }
  return false;
}

/**
 * Wall-plane subdomains that have resolved `wallInfo` and are associated with `roomId`,
 * in the same order as `wallPlane.descriptor.subdomainIndex.all()`.
 */
export function collectWallPlaneSubdomainsForRoomEdge(
  building: Building,
  wallPlane: WallPlane,
  roomId: string,
): WallPlaneSubdomain[] {
  const roomIds = new Set<string>([roomId]);
  const { subdomainIndex, subdomains } = wallPlane.descriptor;
  const result: WallPlaneSubdomain[] = [];
  for (const indexEntry of subdomainIndex.all()) {
    const subdomain = subdomains[indexEntry.arrayIndexV][indexEntry.arrayIndexU];
    if (!subdomain.wallInfo) {
      continue;
    }
    if (!faceNodeIdsHaveRoomParentInSet(building.buildingGraph, subdomain.faceNodeIds, roomIds)) {
      continue;
    }
    result.push(subdomain);
  }
  return result;
}

/**
 * Wall-plane subdomains associated with a freeform wall path segment (edge),
 * in the same order as `wallPlane.descriptor.subdomainIndex.all()`.
 */
export function collectWallPlaneSubdomainsForFreeformWallEdge(
  building: Building,
  wallPlane: WallPlane,
  freeformWallId: string,
  edgeId: number,
): WallPlaneSubdomain[] {
  const { faceNodes } = building.buildingGraph;
  const { subdomainIndex, subdomains } = wallPlane.descriptor;
  const result: WallPlaneSubdomain[] = [];
  for (const indexEntry of subdomainIndex.all()) {
    const subdomain = subdomains[indexEntry.arrayIndexV][indexEntry.arrayIndexU];
    if (!subdomain.wallInfo) {
      continue;
    }
    const matchesFreeformEdge = subdomain.faceNodeIds.some((faceNodeId) => {
      const faceNode = faceNodes[faceNodeId];
      return (
        faceNode?.parentHandle.type === "FREEFORM_WALL" &&
        faceNode.parentHandle.freeformWallId === freeformWallId &&
        faceNode.parentHandle.edgeId === edgeId
      );
    });
    if (!matchesFreeformEdge) {
      continue;
    }
    result.push(subdomain);
  }
  return result;
}

/**
 * For wall mass generation, we want both subdomains that have a room volume parent, as
 * well as any subdomains that were created as a part of the aabb of an operator, but are outside of the
 * original building graph domains.
 */
export function subdomainIsOrphanOrHasRoomVolumeParent(
  subdomain: WallPlaneSubdomain,
  buildingGraph: BuildingGraph,
): boolean {
  if (subdomain.faceNodeIds.length === 0) {
    return true;
  }
  return subdomain.faceNodeIds.some((faceNodeId) => {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    return faceNodeGetVolumeNode(faceNode, buildingGraph)?.parentHandle?.type === "ROOM";
  });
}

/**
 * True when any contributing face node is parented by a freeform wall segment.
 * Pure freeform subdomains have no room volume parent.
 */
export function subdomainHasFreeformWallParent(subdomain: WallPlaneSubdomain, buildingGraph: BuildingGraph): boolean {
  return subdomain.faceNodeIds.some((faceNodeId) => {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    return faceNode?.parentHandle.type === "FREEFORM_WALL";
  });
}

/**
 * Same room-volume-parent check as {@link subdomainIsOrphanOrHasRoomVolumeParent}, without
 * that function's "orphan" (empty `faceNodeIds`) short-circuit — callers here need to
 * distinguish "has no room parent" from "has no face nodes at all".
 */
export function subdomainHasRoomVolumeParent(subdomain: WallPlaneSubdomain, buildingGraph: BuildingGraph): boolean {
  return subdomain.faceNodeIds.length > 0 && subdomainIsOrphanOrHasRoomVolumeParent(subdomain, buildingGraph);
}

/**
 * Whether wall-mass emission should treat this subdomain as freeform-owned.
 *
 * Uses the last SET_WALL_CORE operation applied to the cell (rule `sortIndex` order):
 * if that op was matched from a freeform face node, freeform owns the assembly
 * even when a room face also contributes to the same UV cell. Falls back to face-parent
 * attribution when no core op is present.
 *
 * Can't reuse {@link subdomainIsOrphanOrHasRoomVolumeParent}'s "has a room parent" check
 * for this: on an overlap cell, a subdomain can have *both* a room and a freeform parent,
 * and ownership then depends on which rule actually won the core assignment, not on which
 * parent types are merely present.
 */
export function subdomainWallAssemblyOwnedByFreeform(
  subdomain: WallPlaneSubdomain,
  buildingGraph: BuildingGraph,
): boolean {
  let lastCoreFaceNodeId: string | undefined;
  for (const operation of subdomain.operations) {
    if (operation.wallPlaneOperator.operationType !== "SET_WALL_CORE") {
      continue;
    }
    lastCoreFaceNodeId = faceSideHandleGetFaceNodeId(operation.wallPlaneFaceSideHandle);
  }

  if (lastCoreFaceNodeId !== undefined) {
    const faceNode = buildingGraph.faceNodes[lastCoreFaceNodeId];
    return faceNode?.parentHandle.type === "FREEFORM_WALL";
  }

  return (
    subdomainHasFreeformWallParent(subdomain, buildingGraph) && !subdomainHasRoomVolumeParent(subdomain, buildingGraph)
  );
}

function faceSideHandleGetFaceNodeId(handle: BuildingGraphFaceSideHandle): string | undefined {
  const leaf = faceSideHandleLeaf(handle);
  if (leaf.type === "FACE_NODE_SIDE") {
    return leaf.faceSource.faceNodeId;
  }
  return undefined;
}

/**
 * Returns the freeform wall segment identity for a subdomain, if any face node is
 * freeform-parented. When multiple freeform face nodes contribute, the first
 * wins (v1; coincident multi-segment subdomains are unexpected).
 */
export function subdomainGetFreeformWallSegment(
  subdomain: WallPlaneSubdomain,
  buildingGraph: BuildingGraph,
): { freeformWallId: string; edgeId: number } | undefined {
  for (const faceNodeId of subdomain.faceNodeIds) {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    if (faceNode?.parentHandle.type === "FREEFORM_WALL") {
      return {
        freeformWallId: faceNode.parentHandle.freeformWallId,
        edgeId: faceNode.parentHandle.edgeId,
      };
    }
  }
  return undefined;
}

/**
 * For roof wall mass generation, include subdomains attributed to roof volumes that are
 * not also attributed to a room volume.
 */
export function subdomainHasRoofVolumeParentAndNotRoomParent(
  subdomain: WallPlaneSubdomain,
  buildingGraph: BuildingGraph,
): boolean {
  const hasRoofVolumeParent = subdomain.faceNodeIds.some((faceNodeId) => {
    const faceNode = buildingGraph.faceNodes[faceNodeId];
    return faceNodeGetVolumeNode(faceNode, buildingGraph)?.parentHandle?.type === "ROOF";
  });

  // negate the filter for orphan or room volume parent, since we don't want to double
  // add volumes to both the legacy wall system and legacy roof wall system
  return hasRoofVolumeParent && !subdomainIsOrphanOrHasRoomVolumeParent(subdomain, buildingGraph);
}
